import { Controller } from "stimulus"
import dragula from "dragula"

export default class extends Controller {
  static get targets() {
    return ["board", "dropzone", "sectionTemplate", "optionTemplate", "section", "blockDropzone", "blockLibrary", "itemLibrary", "autosaveStatus", "placeholderWarning"]
  }

  connect() {
    this.boundBeforeSubmit = this.refreshHiddenFields.bind(this)
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    this.boundInputChange = this.handleMapperInputChange.bind(this)
    this.boundWindowScroll = this.syncLibraryPanelPosition.bind(this)
    this.boundWindowResize = this.syncLibraryPanelPosition.bind(this)
    this.autosaveTimer = null
    this.autosaveInFlight = false
    this.autosaveQueued = false
    this.autosaveEnabled = true
    this.sectionKeyCounter = 1
    this.reorderMode = false
    this.formElement = this.element.closest("form")
    if (this.formElement) this.formElement.addEventListener("submit", this.boundBeforeSubmit)
    document.addEventListener("click", this.boundDocumentClick)
    this.element.addEventListener("input", this.boundInputChange)
    this.element.addEventListener("change", this.boundInputChange)
    this.initializeSectionKeyCounter()
    this.setupDrag()
    this.setupBlockDrag()
    this.prepareLineItemLibrarySource()
    this.refreshHiddenFields()
    this.updatePlaceholderWarning()
    this.applyReorderModeUi()
    window.addEventListener("scroll", this.boundWindowScroll, { passive: true })
    window.addEventListener("resize", this.boundWindowResize)
    this.syncLibraryPanelPosition()
  }

  disconnect() {
    if (this.formElement && this.boundBeforeSubmit) {
      this.formElement.removeEventListener("submit", this.boundBeforeSubmit)
    }
    if (this.boundInputChange) {
      this.element.removeEventListener("input", this.boundInputChange)
      this.element.removeEventListener("change", this.boundInputChange)
    }
    if (this.boundDocumentClick) {
      document.removeEventListener("click", this.boundDocumentClick)
    }
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer)
    if (this.boundWindowScroll) window.removeEventListener("scroll", this.boundWindowScroll)
    if (this.boundWindowResize) window.removeEventListener("resize", this.boundWindowResize)
    this.deactivateActiveBlockEditor()
    if (this.drake) this.drake.destroy()
    if (this.blockDrake) this.blockDrake.destroy()
  }

  addSection(event) {
    event.preventDefault()
    const node = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true)
    this.boardTarget.appendChild(node)
    this.setupDrag()
    this.setupBlockDrag()
    this.refreshHiddenFields()
    this.applyReorderModeUi()
    this.scheduleAutosave()
  }

  addSectionBelow(event) {
    event.preventDefault()
    const currentSection = event.currentTarget.closest('[data-target~="proposal-mapper.section"]')
    if (!currentSection) return
    const node = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true)
    currentSection.insertAdjacentElement("afterend", node)
    this.setupDrag()
    this.setupBlockDrag()
    this.refreshHiddenFields()
    this.applyReorderModeUi()
    this.scheduleAutosave()
  }

  confirmGenerate(event) {
    this.syncEditorElements()
    this.refreshHiddenFields()

    const unresolved = this.findManualPlaceholders()
    if (unresolved.length > 0) {
      const preview = unresolved.slice(0, 8).map((token) => `- ${token}`).join("\n")
      const more = unresolved.length > 8 ? `\n- ...and ${unresolved.length - 8} more` : ""
      const proceed = window.confirm(
        `Found unresolved manual placeholders ([[...]] / legacy <<...>>) in proposal text:\n\n${preview}${more}\n\nGenerate anyway?`
      )
      if (!proceed) {
        event.preventDefault()
        return
      }
    } else {
      const proceed = window.confirm("Generate a new proposal DOCX now?")
      if (!proceed) {
        event.preventDefault()
      }
    }
  }

  addOption(event) {
    event.preventDefault()
    const section = event.currentTarget.closest('[data-target~="proposal-mapper.section"]')
    if (!section) return

    const row = section.querySelector(".row")
    if (!row) return

    const node = this.optionTemplateTarget.content.firstElementChild.cloneNode(true)
    const nextOptionName = this.nextOptionNameForSection(section)
    const optionInput = node.querySelector('[data-role="option-name"]')
    if (optionInput) optionInput.value = nextOptionName
    const optionDropzone = node.querySelector(".proposal-dropzone")
    if (optionDropzone) optionDropzone.dataset.optionName = nextOptionName
    row.appendChild(node)
    this.setupDrag()
    this.setupBlockDrag()
    this.refreshHiddenFields()
    this.scheduleAutosave()
  }

  removeOption(event) {
    event.preventDefault()
    this.deactivateActiveBlockEditor()
    const optionColumn = event.currentTarget.closest('[data-role="option-column"]')
    if (!optionColumn) return
    const optionDropzone = optionColumn.querySelector(".proposal-dropzone")
    if (!optionDropzone) return

    if (optionDropzone.querySelector(".proposal-card")) {
      window.alert("This option cannot be removed because it still has line items.")
      return
    }

    if (!window.confirm("Remove this option?")) return

    optionColumn.remove()
    this.setupDrag()
    this.setupBlockDrag()
    this.refreshHiddenFields()
    this.scheduleAutosave()
  }

  removeSection(event) {
    event.preventDefault()
    this.deactivateActiveBlockEditor()
    const section = event.currentTarget.closest('[data-target~="proposal-mapper.section"]')
    if (!section) return
    if (section.querySelector('[data-role="option-column"]')) {
      window.alert("This section cannot be removed while it still has options.")
      return
    }

    if (!window.confirm("Remove this section?")) return

    const destinationSection = this.ensureDestinationSection(section)
    const destinationDropzone = this.sectionCoreDropzone(destinationSection)
    if (!destinationDropzone) return

    Array.from(section.querySelectorAll(".proposal-dropzone .proposal-card")).forEach((card) => {
      destinationDropzone.appendChild(card)
    })

    section.remove()
    this.setupDrag()
    this.setupBlockDrag()
    this.refreshHiddenFields()
    this.scheduleAutosave()
  }

  refreshHiddenFields() {
    this.syncEditorElements()
    let sortOrder = 1
    let bucketUid = 1
    this.sectionTargets.forEach((section, sectionIndex) => {
      const sectionKey = this.ensureSectionKey(section)
      const sectionNameInput = section.querySelector('[data-role="section-name"]')
      const sectionName = (sectionNameInput?.value || "Products").trim()
      const sectionSettingNameInput = section.querySelector('[data-role="section-setting-name"]')
      const sectionSuppressHidden = section.querySelector('[data-role="section-suppress-title-hidden"]')
      const sectionSuppressCheckbox = section.querySelector('[data-role="section-suppress-title"]')
      const includeHeaderHidden = section.querySelector('[data-role="section-include-header-hidden"]')
      const includeHeaderCheckbox = section.querySelector('[data-role="section-include-header"]')
      const includeOptionsHidden = section.querySelector('[data-role="section-include-options-hidden"]')
      const includeOptionsCheckbox = section.querySelector('[data-role="section-include-options"]')
      const includeFooterHidden = section.querySelector('[data-role="section-include-footer-hidden"]')
      const includeFooterCheckbox = section.querySelector('[data-role="section-include-footer"]')
      const sectionOrderInput = section.querySelector('[data-role="section-order"]')
      const sectionSettingBase = `proposal_section_settings[${sectionKey}]`
      if (sectionSettingNameInput) {
        sectionSettingNameInput.name = `${sectionSettingBase}[section_name]`
        sectionSettingNameInput.value = sectionName
      }
      if (sectionSuppressHidden) {
        sectionSuppressHidden.name = `${sectionSettingBase}[suppress_title]`
        sectionSuppressHidden.value = "0"
      }
      if (sectionSuppressCheckbox) {
        sectionSuppressCheckbox.name = `${sectionSettingBase}[suppress_title]`
      }
      if (includeHeaderHidden) {
        includeHeaderHidden.name = `${sectionSettingBase}[include_header]`
        includeHeaderHidden.value = "0"
      }
      if (includeHeaderCheckbox) includeHeaderCheckbox.name = `${sectionSettingBase}[include_header]`
      if (includeOptionsHidden) {
        includeOptionsHidden.name = `${sectionSettingBase}[include_options]`
        includeOptionsHidden.value = "0"
      }
      if (includeOptionsCheckbox) includeOptionsCheckbox.name = `${sectionSettingBase}[include_options]`
      if (includeFooterHidden) {
        includeFooterHidden.name = `${sectionSettingBase}[include_footer]`
        includeFooterHidden.value = "0"
      }
      if (includeFooterCheckbox) includeFooterCheckbox.name = `${sectionSettingBase}[include_footer]`
      if (sectionOrderInput) {
        sectionOrderInput.name = `${sectionSettingBase}[order]`
        sectionOrderInput.value = String(sectionIndex)
      }

      this.applySectionVisibility(section)

      section.querySelectorAll(".proposal-dropzone").forEach((dropzone) => {
        const bucketKind = dropzone.dataset.bucketKind === "option" ? "option" : "core"
        const optionName = bucketKind === "option" ? this.optionNameForDropzone(dropzone) : this.coreOptionNameForSection(section)
        dropzone.dataset.optionName = optionName
        dropzone.dataset.sectionName = sectionName
        dropzone.dataset.bucketUid = String(bucketUid)
        bucketUid += 1

        dropzone.querySelectorAll(".proposal-card").forEach((card) => {
          this.setHidden(card, "proposal-section-key", sectionName)
          this.setHidden(card, "proposal-group-kind", bucketKind)
          this.setHidden(card, "proposal-group-name", bucketKind === "core" ? optionName : (bucketKind === "option" ? optionName : ""))
          this.setHidden(card, "proposal-sort-order", String(sortOrder))
          sortOrder += 1
        })
      })

      section.querySelectorAll(".proposal-block-dropzone").forEach((dropzone) => {
        const area = dropzone.closest(".proposal-text-block-area")
        const bucketKind = (area?.dataset.bucketKind || "core").toLowerCase()
        const placement = (area?.dataset.placement || "header").toLowerCase()
        const optionName = bucketKind === "option" ? this.optionNameForArea(area) : this.coreOptionNameForSection(section)
        dropzone.dataset.sectionName = sectionName
        dropzone.dataset.bucketUid = String(bucketUid)
        this.refreshAllocatedBlockFields(dropzone, {
          sectionKey: sectionKey,
          sectionName: sectionName,
          bucketKind: bucketKind,
          optionName: optionName,
          placement: placement,
          bucketUid: String(bucketUid)
        })
        bucketUid += 1
      })
    })
    this.refreshMoveMenus()
    this.updatePlaceholderWarning()
  }

  applySectionVisibility(section) {
    const includeHeader = section.querySelector('[data-role="section-include-header"]')?.checked !== false
    const includeOptions = section.querySelector('[data-role="section-include-options"]')?.checked !== false
    const includeFooter = section.querySelector('[data-role="section-include-footer"]')?.checked !== false

    const headerWrap = section.querySelector('[data-role="section-header-wrap"]')
    const optionsWrap = section.querySelector('[data-role="section-options-wrap"]')
    const footerWrap = section.querySelector('[data-role="section-footer-wrap"]')
    const addOptionBtn = section.querySelector('[data-action="proposal-mapper#addOption"]')

    if (headerWrap) headerWrap.classList.toggle("d-none", !includeHeader)
    if (optionsWrap) optionsWrap.classList.toggle("d-none", !includeOptions)
    if (footerWrap) footerWrap.classList.toggle("d-none", !includeFooter)
    if (addOptionBtn) addOptionBtn.classList.toggle("d-none", !includeOptions || this.reorderMode)
  }

  ensureSectionKey(section) {
    if (!section) return ""
    const existing = (section.dataset.sectionKey || "").trim()
    if (existing.length > 0) return existing
    const key = `section-${this.sectionKeyCounter}`
    this.sectionKeyCounter += 1
    section.dataset.sectionKey = key
    return key
  }

  initializeSectionKeyCounter() {
    let maxIndex = 0
    this.sectionTargets.forEach((section) => {
      const key = (section.dataset.sectionKey || "").trim()
      const match = key.match(/^section-(\d+)$/)
      if (!match) return
      const num = parseInt(match[1], 10)
      if (!Number.isNaN(num) && num > maxIndex) maxIndex = num
    })
    this.sectionKeyCounter = maxIndex + 1
  }

  setHidden(card, role, value) {
    const input = card.querySelector(`[data-role="${role}"]`)
    if (input) input.value = value
  }

  refreshAllocatedBlockFields(dropzone, context) {
    const area = dropzone.closest(".proposal-text-block-area")
    if (!area) return

    const sectionKey = context.sectionKey || ""
    const sectionName = context.sectionName || ""
    const bucketKind = context.bucketKind === "option" ? "option" : (context.bucketKind === "section" ? "section" : "core")
    const optionName = context.optionName || ""
    const placement = context.placement === "footer" ? "footer" : "header"
    const bucketUid = context.bucketUid || "0"
    const base = `allocated_text_blocks[${bucketUid}]`
    const sectionInput = area.querySelector('[data-role="allocated-section-name"]')
    const sectionKeyInput = area.querySelector('[data-role="allocated-section-key"]')
    const kindInput = area.querySelector('[data-role="allocated-group-kind"]')
    const nameInput = area.querySelector('[data-role="allocated-group-name"]')
    const placementInput = area.querySelector('[data-role="allocated-placement"]')

    if (sectionKeyInput) {
      sectionKeyInput.name = `${base}[section_key]`
      sectionKeyInput.value = sectionKey
    }
    if (sectionInput) {
      sectionInput.name = `${base}[section_name]`
      sectionInput.value = sectionName
    }
    if (kindInput) {
      kindInput.name = `${base}[group_kind]`
      kindInput.value = bucketKind
    }
    if (nameInput) {
      nameInput.name = `${base}[group_name]`
      nameInput.value = bucketKind === "section" ? "" : optionName
    }
    if (placementInput) {
      placementInput.name = `${base}[placement]`
      placementInput.value = placement
    }

    area.querySelectorAll(".proposal-block-card").forEach((card, index) => {
      const idInput = card.querySelector('[data-role="allocated-block-id"]')
      const titleInput = card.querySelector('[data-role="allocated-block-title"]')
      const bodyInput = card.querySelector('[data-role="allocated-block-body"]')
      if (idInput) idInput.name = `${base}[blocks][${index}][id]`
      if (titleInput) titleInput.name = `${base}[blocks][${index}][title]`
      if (bodyInput) bodyInput.name = `${base}[blocks][${index}][body_text]`
    })
  }

  setupDrag() {
    if (this.drake) this.drake.destroy()

    const containers = Array.from(this.element.querySelectorAll(".proposal-dropzone"))
    if (containers.length === 0) return

    const itemLibrary = this.hasItemLibraryTarget ? this.itemLibraryTarget : null
    this.drake = dragula(containers, {
      copy: (_el, source) => !!itemLibrary && source === itemLibrary,
      accepts: (_el, target) => target && target.classList.contains("proposal-dropzone"),
      moves: (el) => el && el.classList.contains("proposal-card")
    })
      .on("drop", (el, target, source) => {
        if (!target || (itemLibrary && target === itemLibrary)) {
          if (source === itemLibrary) {
            el.remove()
          } else if (itemLibrary && target === itemLibrary) {
            const compact = this.toCompactLibraryCard(el)
            if (compact) this.prepareLineItemLibrarySource()
          }
          this.refreshHiddenFields()
          this.scheduleAutosave()
          this.performAutosave()
          return
        }
        if (source === itemLibrary) {
          const expanded = this.expandLineItemCardFromTemplate(el) || el
          this.restoreLineItemCardFromLibrary(expanded)
          this.removeLineItemFromLibrary(expanded.dataset.lineItemId)
          this.removeDuplicateLineItems(expanded)
        }
        this.refreshHiddenFields()
        this.scheduleAutosave()
      })
      .on("cancel", () => { this.refreshHiddenFields(); this.scheduleAutosave() })
      .on("remove", () => { this.refreshHiddenFields(); this.scheduleAutosave() })
  }

  removeDuplicateLineItems(card) {
    const lineItemId = card?.dataset?.lineItemId
    if (!lineItemId) return
    this.element.querySelectorAll(`.proposal-dropzone .proposal-card[data-line-item-id="${lineItemId}"]`).forEach((node) => {
      if (node !== card) node.remove()
    })
  }

  prepareLineItemLibrarySource() {
    if (!this.hasItemLibraryTarget) return
    this.itemLibraryTarget.querySelectorAll(".proposal-card").forEach((card) => {
      card.dataset.librarySource = "1"
      card.querySelectorAll("input[name], select[name], textarea[name]").forEach((node) => {
        if (!node.dataset.originalName) node.dataset.originalName = node.name
        node.removeAttribute("name")
      })

      const moveSelect = card.querySelector('[data-role="move-to"]')
      if (moveSelect) {
        moveSelect.disabled = true
        moveSelect.classList.add("d-none")
      }
      const cadence = card.querySelector('[data-role="cadence"]')
      if (cadence) cadence.disabled = true
    })
  }

  restoreLineItemCardFromLibrary(card) {
    if (!card) return
    card.querySelectorAll("[data-original-name]").forEach((node) => {
      node.name = node.dataset.originalName
    })
    delete card.dataset.librarySource

    const moveSelect = card.querySelector('[data-role="move-to"]')
    if (moveSelect) {
      moveSelect.disabled = false
      moveSelect.classList.remove("d-none")
      moveSelect.value = ""
    }
    const cadence = card.querySelector('[data-role="cadence"]')
    if (cadence) cadence.disabled = false
  }

  expandLineItemCardFromTemplate(card) {
    const lineItemId = card?.dataset?.lineItemId
    if (!lineItemId) return card
    const template = this.element.querySelector(`template[data-role="line-item-template"][data-line-item-id="${lineItemId}"]`)
    const fullCard = template?.content?.firstElementChild?.cloneNode(true)
    if (!fullCard) return card
    card.replaceWith(fullCard)
    return fullCard
  }

  removeLineItemFromLibrary(lineItemId) {
    if (!this.hasItemLibraryTarget || !lineItemId) return
    const existing = this.itemLibraryTarget.querySelector(`.proposal-card[data-line-item-id="${lineItemId}"]`)
    if (existing) existing.remove()
  }

  addLineItemBackToLibrary(card) {
    if (!this.hasItemLibraryTarget) return
    const lineItemId = card?.dataset?.lineItemId
    if (!lineItemId) return
    const existing = this.itemLibraryTarget.querySelector(`.proposal-card[data-line-item-id="${lineItemId}"]`)
    if (existing) return
    const template = this.element.querySelector(`template[data-role="line-item-compact-template"][data-line-item-id="${lineItemId}"]`)
    const compactCard = template?.content?.firstElementChild?.cloneNode(true)
    if (!compactCard) return
    this.itemLibraryTarget.appendChild(compactCard)
    this.prepareLineItemLibrarySource()
  }

  toCompactLibraryCard(card) {
    const lineItemId = card?.dataset?.lineItemId
    if (!lineItemId) return null
    const template = this.element.querySelector(`template[data-role="line-item-compact-template"][data-line-item-id="${lineItemId}"]`)
    const compactCard = template?.content?.firstElementChild?.cloneNode(true)
    if (!compactCard) return null
    card.replaceWith(compactCard)
    return compactCard
  }

  setupBlockDrag() {
    if (this.blockDrake) this.blockDrake.destroy()
    if (!this.hasBlockLibraryTarget) return

    const libraryContainers = Array.from(new Set(
      Array.from(this.blockLibraryTarget.querySelectorAll(".proposal-library-card"))
        .map((card) => card.parentElement)
        .filter((parent) => parent)
    ))
    if (libraryContainers.length === 0) return

    const containers = libraryContainers.concat(this.blockDropzoneTargets)
    if (containers.length <= libraryContainers.length) return

    this.blockDrake = dragula(containers, {
      copy: (_el, source) => libraryContainers.includes(source),
      moves: (el, source) => {
        if (!source) return false
        if (libraryContainers.includes(source)) return el.classList.contains("proposal-library-card")
        return el.classList.contains("proposal-block-card")
      },
      accepts: (_el, target) => target && target.classList.contains("proposal-block-dropzone")
    })
      .on("drag", () => this.deactivateActiveBlockEditor())
      .on("drop", (el, target, source) => {
        if (!target || libraryContainers.includes(target)) {
          if (libraryContainers.includes(target)) el.remove()
          this.refreshHiddenFields()
          this.scheduleAutosave()
          return
        }
        if (libraryContainers.includes(source)) this.prepareAllocatedBlockCard(el)
        this.refreshHiddenFields()
        this.scheduleAutosave()
      })
      .on("cancel", () => { this.refreshHiddenFields(); this.scheduleAutosave() })
      .on("remove", () => { this.refreshHiddenFields(); this.scheduleAutosave() })
  }

  prepareAllocatedBlockCard(card) {
    if (!card.classList.contains("proposal-library-card")) {
      const nested = card.querySelector(".proposal-library-card")
      if (nested) card = nested
    }
    const blockId = card.dataset.blockId || ""
    const blockTitle = card.dataset.blockTitle || ""
    const blockBody = this.applyTemplatePlaceholders(card.dataset.blockBody || "")
    const replacement = this.createAllocatedBlockCard({ id: blockId, title: blockTitle, body: blockBody })
    card.replaceWith(replacement)
  }

  applyTemplatePlaceholders(text) {
    const firstName = (this.element.dataset.customerFirstName || "").trim()
    const fullName = (this.element.dataset.customerFullName || "").trim()
    const companyName = (this.element.dataset.companyName || "").trim()

    const replacements = {
      FIRSTNAME: firstName,
      FIRST_NAME: firstName,
      FULLNAME: fullName,
      FULL_NAME: fullName,
      "USER NAME": (this.element.dataset.userName || "").trim(),
      USER_NAME: (this.element.dataset.userName || "").trim(),
      "USER JOBTITLE": (this.element.dataset.userJobTitle || "").trim(),
      USER_JOBTITLE: (this.element.dataset.userJobTitle || "").trim(),
      "COMPANY NAME": companyName,
      COMPANY_NAME: companyName,
      COMPANY: companyName
    }

    return (text || "").replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, tokenRaw) => {
      const key = tokenRaw.toString().trim().toUpperCase()
      return Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : _match
    })
  }

  addBlock(event) {
    event.preventDefault()
    const area = event.currentTarget.closest(".proposal-text-block-area")
    if (!area) return
    const dropzone = area.querySelector(".proposal-block-dropzone")
    if (!dropzone) return
    const card = this.createAllocatedBlockCard({
      id: "",
      title: "New Block",
      body: ""
    })
    dropzone.appendChild(card)
    this.refreshHiddenFields()
    const preview = card.querySelector('[data-role="allocated-block-preview"]')
    if (preview) this.activateBlockEditor({ currentTarget: preview })
  }

  createAllocatedBlockCard({ id, title, body }) {
    const card = document.createElement("div")
    card.className = "proposal-block-card card mb-2"
    card.dataset.blockBodyValue = body || ""

    const cardBody = document.createElement("div")
    cardBody.className = "card-body p-2"

    const row = document.createElement("div")
    row.className = "d-flex align-items-center justify-content-between mb-1"
    row.style.gap = "6px"

    const titleLabel = document.createElement("div")
    titleLabel.className = "small text-muted"
    titleLabel.setAttribute("data-role", "allocated-block-title-label")
    titleLabel.textContent = title || "Block"
    row.appendChild(titleLabel)

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "btn btn-outline-danger btn-xs"
    removeBtn.style.padding = "0.08rem 0.35rem"
    removeBtn.style.fontSize = "0.72rem"
    removeBtn.textContent = "Remove"
    removeBtn.setAttribute("data-action", "proposal-mapper#removeAllocatedBlock")
    row.appendChild(removeBtn)
    cardBody.appendChild(row)

    const preview = document.createElement("div")
    preview.className = "border rounded p-2 small"
    preview.style.minHeight = "52px"
    preview.style.background = "#fff"
    preview.setAttribute("data-role", "allocated-block-preview")
    preview.setAttribute("data-action", "click->proposal-mapper#activateBlockEditor")
    preview.innerHTML = this.sanitizePreviewHtml(body || "")
    cardBody.appendChild(preview)

    const editorWrap = document.createElement("div")
    editorWrap.className = "mt-1 d-none"
    editorWrap.setAttribute("data-role", "allocated-block-editor-wrap")
    editorWrap.innerHTML = `
      <div class="mb-1">
        <button type="button" class="btn btn-outline-secondary btn-xs" data-action="proposal-mapper#formatBlockText" data-format-command="bold">B</button>
        <button type="button" class="btn btn-outline-secondary btn-xs" data-action="proposal-mapper#formatBlockText" data-format-command="italic">I</button>
        <button type="button" class="btn btn-outline-secondary btn-xs" data-action="proposal-mapper#formatBlockText" data-format-command="insertUnorderedList">Bullets</button>
        <button type="button" class="btn btn-outline-secondary btn-xs" data-action="proposal-mapper#formatBlockText" data-format-command="justifyLeft">Left</button>
        <button type="button" class="btn btn-outline-secondary btn-xs" data-action="proposal-mapper#formatBlockText" data-format-command="justifyCenter">Center</button>
        <button type="button" class="btn btn-outline-secondary btn-xs" data-action="proposal-mapper#formatBlockText" data-format-command="formatBlock" data-format-value="h2">H2</button>
      </div>
      <div class="form-control form-control-sm" style="min-height: 90px; height: auto; overflow-y: auto;" contenteditable="true" data-cke-ignore-events="true" data-cke-disable-auto-inline="true" data-role="allocated-block-editor"></div>
      <input type="hidden" data-role="allocated-block-body" />
    `
    const bodyInput = editorWrap.querySelector('[data-role="allocated-block-body"]')
    if (bodyInput) bodyInput.value = body || ""
    cardBody.appendChild(editorWrap)

    const titleInput = document.createElement("input")
    titleInput.type = "hidden"
    titleInput.value = title || ""
    titleInput.setAttribute("data-role", "allocated-block-title")
    cardBody.appendChild(titleInput)

    const idInput = document.createElement("input")
    idInput.type = "hidden"
    idInput.value = id || ""
    idInput.setAttribute("data-role", "allocated-block-id")
    cardBody.appendChild(idInput)

    card.appendChild(cardBody)
    return card
  }

  removeAllocatedBlock(event) {
    event.preventDefault()
    const card = event.currentTarget.closest(".proposal-block-card")
    if (!card) return
    if (this.activeBlockCard === card) this.deactivateBlockEditor(card)
    card.remove()
    this.refreshHiddenFields()
    this.scheduleAutosave()
  }

  activateBlockEditor(event) {
    const card = event.currentTarget.closest(".proposal-block-card")
    if (!card) return
    if (this.activeBlockCard && this.activeBlockCard !== card) this.deactivateBlockEditor(this.activeBlockCard)

    const preview = card.querySelector('[data-role="allocated-block-preview"]')
    const editorWrap = card.querySelector('[data-role="allocated-block-editor-wrap"]')
    const bodyInput = card.querySelector('[data-role="allocated-block-body"]')
    const editorDiv = card.querySelector('[data-role="allocated-block-editor"]')
    if (!bodyInput || !editorWrap || !editorDiv) return

    const previewHtml = preview ? preview.innerHTML : ""
    const storedBody = card.dataset.blockBodyValue || ""
    const initialContent = this.resolveBlockEditorContent(bodyInput.value, previewHtml, storedBody)
    bodyInput.value = initialContent

    if (preview) preview.classList.add("d-none")
    editorWrap.classList.remove("d-none")
    editorDiv.innerHTML = initialContent || ""
    editorDiv.focus()

    this.activeBlockCard = card
  }

  deactivateBlockEditor(card) {
    if (!card) return

    const preview = card.querySelector('[data-role="allocated-block-preview"]')
    const editorWrap = card.querySelector('[data-role="allocated-block-editor-wrap"]')
    const bodyInput = card.querySelector('[data-role="allocated-block-body"]')
    const editorDiv = card.querySelector('[data-role="allocated-block-editor"]')
    if (!bodyInput || !editorWrap || !editorDiv) return

    bodyInput.value = (editorDiv.innerHTML || "").toString()
    card.dataset.blockBodyValue = bodyInput.value.toString()

    if (preview) {
      preview.innerHTML = this.sanitizePreviewHtml(bodyInput.value)
      preview.classList.remove("d-none")
    }
    editorWrap.classList.add("d-none")
    if (this.activeBlockCard === card) this.activeBlockCard = null
    this.scheduleAutosave()
  }

  deactivateActiveBlockEditor() {
    if (!this.activeBlockCard) return
    this.deactivateBlockEditor(this.activeBlockCard)
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.deactivateActiveBlockEditor()
      return
    }
    const clickedCard = event.target.closest(".proposal-block-card")
    if (!clickedCard) this.deactivateActiveBlockEditor()
  }

  syncEditorElements() {
    if (!this.activeBlockCard) return
    const bodyInput = this.activeBlockCard.querySelector('[data-role="allocated-block-body"]')
    const editorDiv = this.activeBlockCard.querySelector('[data-role="allocated-block-editor"]')
    if (!bodyInput || !editorDiv) return
    bodyInput.value = (editorDiv.innerHTML || "").toString()
  }

  sanitizePreviewHtml(html) {
    const temp = document.createElement("div")
    temp.innerHTML = html || ""
    temp.querySelectorAll("script,style,iframe,object,embed,form").forEach((node) => node.remove())
    temp.querySelectorAll("*").forEach((node) => {
      Array.from(node.attributes).forEach((attr) => {
        if (/^on/i.test(attr.name)) node.removeAttribute(attr.name)
      })
    })
    const cleaned = temp.innerHTML.trim()
    return cleaned.length > 0 ? cleaned : "<span class=\"text-muted\">Click to edit block text...</span>"
  }

  resolveBlockEditorContent(bodyValue, previewHtml, storedBody) {
    const fromBody = (bodyValue || "").toString()
    if (fromBody.trim().length > 0) return fromBody

    const fromPreview = this.stripEditorPlaceholder(previewHtml)
    if (fromPreview.trim().length > 0) return fromPreview

    const fromStored = (storedBody || "").toString()
    if (fromStored.trim().length > 0) return fromStored
    return ""
  }

  stripEditorPlaceholder(html) {
    const temp = document.createElement("div")
    temp.innerHTML = html || ""
    temp.querySelectorAll(".text-muted").forEach((node) => {
      if ((node.textContent || "").trim() === "Click to edit block text...") node.remove()
    })
    return temp.innerHTML
  }

  formatBlockText(event) {
    event.preventDefault()
    const command = event.currentTarget.dataset.formatCommand
    const value = event.currentTarget.dataset.formatValue || null
    if (!command) return
    if (command === "formatBlock" && value) {
      document.execCommand(command, false, `<${value}>`)
    } else {
      document.execCommand(command, false, value)
    }
    this.syncEditorElements()
    this.scheduleAutosave()
  }

  moveCard(event) {
    const select = event.currentTarget
    const targetUid = select.value
    if (!targetUid) return

    const card = select.closest(".proposal-card")
    const dropzone = this.element.querySelector(`.proposal-dropzone[data-bucket-uid="${targetUid}"]`)
    if (!card || !dropzone) return

    dropzone.appendChild(card)
    select.value = ""
    this.refreshHiddenFields()
    this.scheduleAutosave()
  }

  refreshMoveMenus() {
    const bucketOptions = Array.from(this.element.querySelectorAll(".proposal-dropzone")).map((dropzone) => {
      const sectionName = (dropzone.dataset.sectionName || "Section").trim()
      const optionName = (dropzone.dataset.optionName || (dropzone.dataset.bucketKind === "option" ? "Option" : "Option 1")).trim()
      return {
        uid: dropzone.dataset.bucketUid,
        label: `${sectionName} -> ${optionName}`
      }
    })

    this.element.querySelectorAll('.proposal-card [data-role="move-to"]').forEach((select) => {
      const card = select.closest(".proposal-card")
      const currentDropzone = card?.closest(".proposal-dropzone")
      const currentUid = currentDropzone?.dataset.bucketUid

      select.innerHTML = ""
      const placeholder = document.createElement("option")
      placeholder.value = ""
      placeholder.textContent = "Move to..."
      select.appendChild(placeholder)

      bucketOptions.forEach((optionData) => {
        if (optionData.uid === currentUid) return
        const option = document.createElement("option")
        option.value = optionData.uid
        option.textContent = optionData.label
        select.appendChild(option)
      })
    })
  }

  nextOptionNameForSection(section) {
    const nums = []
    section.querySelectorAll('[data-role="option-name"]').forEach((input) => {
      const value = (input.value || "").trim()
      const match = value.match(/^Option\s+(\d+)$/i)
      if (match) nums.push(parseInt(match[1], 10))
    })

    const maxNum = nums.length > 0 ? Math.max(...nums) : 1
    return `Option ${maxNum + 1}`
  }

  optionNameForDropzone(dropzone) {
    const optionNameInput = dropzone.closest(".border")?.querySelector('[data-role="option-name"]')
    return (optionNameInput?.value || "Option").trim()
  }

  optionNameForArea(area) {
    const optionNameInput = area?.closest(".proposal-bucket")?.querySelector('[data-role="option-name"]')
    return (optionNameInput?.value || "Option").trim()
  }

  coreOptionNameForSection(section) {
    const coreInput = section?.querySelector('[data-role="core-option-name"]')
    return (coreInput?.value || "Option 1").trim()
  }

  moveSectionUp(event) {
    event.preventDefault()
    if (!this.reorderMode) return
    const section = event.currentTarget.closest('[data-target~="proposal-mapper.section"]')
    if (!section) return
    const prev = section.previousElementSibling
    if (!prev) return
    this.boardTarget.insertBefore(section, prev)
    this.refreshHiddenFields()
    this.scheduleAutosave()
  }

  moveSectionDown(event) {
    event.preventDefault()
    if (!this.reorderMode) return
    const section = event.currentTarget.closest('[data-target~="proposal-mapper.section"]')
    if (!section) return
    const next = section.nextElementSibling
    if (!next) return
    this.boardTarget.insertBefore(next, section)
    this.refreshHiddenFields()
    this.scheduleAutosave()
  }

  startReorderMode(event) {
    event.preventDefault()
    this.reorderMode = true
    this.deactivateActiveBlockEditor()
    this.applyReorderModeUi()
  }

  finishReorderMode(event) {
    event.preventDefault()
    this.reorderMode = false
    this.applyReorderModeUi()
    this.refreshHiddenFields()
    this.scheduleAutosave()
  }

  applyReorderModeUi() {
    const startButton = this.element.querySelector('[data-role="reorder-start"]')
    const finishButton = this.element.querySelector('[data-role="reorder-finish"]')
    const normalTopActions = this.element.querySelectorAll('[data-role="reorder-normal-action"]')

    if (startButton) startButton.classList.toggle("d-none", this.reorderMode)
    if (finishButton) finishButton.classList.toggle("d-none", !this.reorderMode)
    normalTopActions.forEach((node) => node.classList.toggle("d-none", this.reorderMode))

    this.sectionTargets.forEach((section) => {
      const sectionBody = section.querySelector(".card-body")
      const sectionName = section.querySelector('[data-role="section-name"]')
      const moveUp = section.querySelector('[data-role="section-move-up"]')
      const moveDown = section.querySelector('[data-role="section-move-down"]')
      const normalActions = section.querySelectorAll('[data-role="section-normal-action"]')
      const settingsLabels = [
        section.querySelector('[data-role="section-include-header"]')?.closest("label"),
        section.querySelector('[data-role="section-include-options"]')?.closest("label"),
        section.querySelector('[data-role="section-include-footer"]')?.closest("label"),
        section.querySelector('[data-role="section-suppress-title"]')?.closest("label")
      ].filter((node) => !!node)

      if (sectionBody) sectionBody.classList.toggle("d-none", this.reorderMode)
      if (sectionName) {
        sectionName.readOnly = this.reorderMode
        sectionName.classList.toggle("bg-light", this.reorderMode)
      }
      if (moveUp) moveUp.classList.toggle("d-none", !this.reorderMode)
      if (moveDown) moveDown.classList.toggle("d-none", !this.reorderMode)
      settingsLabels.forEach((label) => label.classList.toggle("d-none", this.reorderMode))
      normalActions.forEach((node) => node.classList.toggle("d-none", this.reorderMode))
    })
  }

  handleMapperInputChange(event) {
    if (!event.target) return
    if (!this.element.contains(event.target)) return
    this.updatePlaceholderWarning()
    this.scheduleAutosave()
  }

  scheduleAutosave() {
    if (!this.autosaveEnabled) return
    if (!this.formElement) return
    if (!this.element.dataset.autosaveUrl) return

    if (this.autosaveTimer) clearTimeout(this.autosaveTimer)
    this.setAutosaveStatus("Saving...", "text-muted")
    this.autosaveTimer = window.setTimeout(() => this.performAutosave(), 700)
  }

  performAutosave() {
    if (this.autosaveInFlight) {
      this.autosaveQueued = true
      return
    }

    this.autosaveInFlight = true
    this.syncEditorElements()
    const data = new FormData(this.formElement)
    data.set("proposal_submit_action", "save")
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""

    fetch(this.element.dataset.autosaveUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken
      },
      body: data
    })
      .then((response) => response.json().then((json) => ({ status: response.status, json: json })))
      .then(({ status, json }) => {
        if (status >= 200 && status < 300 && json && json.ok) {
          const stamp = this.formatAutosaveTime(json.saved_at)
          this.setAutosaveStatus(`Saved ${stamp}`, "text-success")
        } else {
          const msg = json && json.error ? json.error : "Autosave failed"
          this.setAutosaveStatus(msg, "text-danger")
        }
      })
      .catch(() => {
        this.setAutosaveStatus("Autosave failed", "text-danger")
      })
      .finally(() => {
        this.autosaveInFlight = false
        this.updatePlaceholderWarning()
        if (this.autosaveQueued) {
          this.autosaveQueued = false
          this.scheduleAutosave()
        }
      })
  }

  setAutosaveStatus(text, klass) {
    if (!this.hasAutosaveStatusTarget) return
    this.autosaveStatusTarget.textContent = text
    this.autosaveStatusTarget.classList.remove("text-muted", "text-success", "text-danger")
    this.autosaveStatusTarget.classList.add(klass)
  }

  formatAutosaveTime(iso8601) {
    const date = new Date(iso8601)
    if (Number.isNaN(date.getTime())) return ""
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  }

  syncLibraryPanelPosition() {
    const column = this.element.querySelector('[data-role="library-column"]')
    const panel = this.element.querySelector('[data-role="library-panel"]')
    if (!column || !panel) return

    if (window.innerWidth < 992) {
      panel.style.position = ""
      panel.style.top = ""
      panel.style.left = ""
      panel.style.width = ""
      panel.style.maxHeight = ""
      panel.style.overflowY = ""
      return
    }

    const rect = column.getBoundingClientRect()
    const reservedTop = this.computeReservedTopOffset()
    const desiredTop = Math.max(rect.top, reservedTop + 120)
    const maxHeight = Math.max(220, window.innerHeight - desiredTop - 10)

    panel.style.position = "fixed"
    panel.style.top = `${desiredTop}px`
    panel.style.left = `${Math.max(rect.left, 0)}px`
    panel.style.width = `${rect.width}px`
    panel.style.maxHeight = `${maxHeight}px`
    panel.style.overflowY = "auto"
  }

  computeReservedTopOffset() {
    const selectors = [
      ".navbar-fixed-top",
      ".navbar.fixed-top",
      "header .navbar",
      "#fixed-sidebar"
    ]

    let offset = 0
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        const style = window.getComputedStyle(el)
        const isFixedOrSticky = style.position === "fixed" || style.position === "sticky"
        if (!isFixedOrSticky) return
        const rect = el.getBoundingClientRect()
        offset = Math.max(offset, rect.bottom)
      })
    })
    return offset
  }

  findManualPlaceholders() {
    const squareRegex = /\[\[\s*([^[\]]+?)\s*\]\]/g
    const squareEncodedRegex = /(?:&#91;|&lbrack;){2}\s*([^<>]+?)\s*(?:&#93;|&rbrack;){2}/gi
    const legacyRegex = /<<\s*([^<>]+?)\s*>>/g
    const legacyEncodedRegex = /&lt;&lt;\s*([^<>]+?)\s*&gt;&gt;/g
    const seen = new Set()
    const values = []

    this.element.querySelectorAll('textarea, input[type="text"], input[type="hidden"], [contenteditable="true"], [data-role="allocated-block-preview"]').forEach((node) => {
      const text = (node.value || "").toString()
      const html = (node.innerHTML || "").toString()
      const contentText = (node.textContent || "").toString()
      if (text.length > 0) values.push(text)
      if (html.length > 0) values.push(html)
      if (contentText.length > 0) values.push(contentText)
    })

    values.forEach((text) => {
      let match
      while ((match = squareRegex.exec(text)) !== null) {
        seen.add(`[[${match[1].trim()}]]`)
      }
      while ((match = squareEncodedRegex.exec(text)) !== null) {
        seen.add(`[[${match[1].trim()}]]`)
      }
      while ((match = legacyRegex.exec(text)) !== null) {
        seen.add(`<<${match[1].trim()}>>`)
      }
      while ((match = legacyEncodedRegex.exec(text)) !== null) {
        seen.add(`<<${match[1].trim()}>>`)
      }
    })

    return Array.from(seen)
  }

  updatePlaceholderWarning() {
    if (!this.hasPlaceholderWarningTarget) return

    const unresolved = this.findManualPlaceholders()
    if (unresolved.length === 0) {
      this.placeholderWarningTarget.classList.add("d-none")
      this.placeholderWarningTarget.textContent = ""
      return
    }

    const preview = unresolved.slice(0, 5).join(", ")
    const suffix = unresolved.length > 5 ? ` (+${unresolved.length - 5} more)` : ""
    this.placeholderWarningTarget.textContent = `Warning: unresolved manual placeholders detected ([[...]] / legacy <<...>>) (${unresolved.length}): ${preview}${suffix}`
    this.placeholderWarningTarget.classList.remove("d-none")
  }

  sectionCoreDropzone(section) {
    return section.querySelector('.proposal-dropzone[data-bucket-kind="core"]')
  }

  ensureDestinationSection(sectionToRemove) {
    const candidates = this.sectionTargets.filter((section) => section !== sectionToRemove)
    if (candidates.length > 0) return candidates[0]

    const node = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true)
    const nameInput = node.querySelector('[data-role="section-name"]')
    if (nameInput) nameInput.value = "Products"
    this.boardTarget.appendChild(node)
    return node
  }
}
